class SkipperAIEstimator extends HTMLElement {
  constructor() {
    super();
    this.apiKey = this.dataset.apiKey || '';
    this.proxyUrl = this.dataset.proxyUrl || '';
    this.pricePerSqm = parseFloat(this.dataset.pricePerSqm) || 180;
    this.minPrice = parseFloat(this.dataset.minPrice) || 250;
    this.bookingUrl = this.dataset.bookingUrl || '/pages/book-a-fitment';
    this.shopUrl = this.dataset.shopUrl || '/collections/all';
    this.mode = 'photo';
  }

  connectedCallback() {
    this.uploadZone = this.querySelector('[data-upload-zone]');
    this.fileInput = this.querySelector('[data-file-input]');
    this.preview = this.querySelector('[data-preview]');
    this.previewImg = this.querySelector('[data-preview-img]');
    this.analyseBtn = this.querySelector('[data-analyse-btn]');
    this.loadingState = this.querySelector('[data-loading]');
    this.resultState = this.querySelector('[data-result]');
    this.errorState = this.querySelector('[data-error]');
    this.resetBtns = this.querySelectorAll('[data-reset-btn]');
    this.modelPanel = this.querySelector('[data-model-panel]');
    this.modelInput = this.querySelector('[data-model-input]');
    this.modelBtn = this.querySelector('[data-model-btn]');
    this.tabs = this.querySelectorAll('[data-tab]');

    this.tabs.forEach(tab => tab.addEventListener('click', () => this.switchTab(tab.dataset.tab)));

    this.uploadZone?.addEventListener('click', () => this.fileInput?.click());
    this.uploadZone?.addEventListener('dragover', (e) => { e.preventDefault(); this.uploadZone.classList.add('dragover'); });
    this.uploadZone?.addEventListener('dragleave', () => this.uploadZone.classList.remove('dragover'));
    this.uploadZone?.addEventListener('drop', (e) => { e.preventDefault(); this.uploadZone.classList.remove('dragover'); this.handleFile(e.dataTransfer.files[0]); });
    this.fileInput?.addEventListener('change', (e) => this.handleFile(e.target.files[0]));
    this.analyseBtn?.addEventListener('click', () => this.analyseImage());
    this.resetBtns.forEach(btn => btn.addEventListener('click', () => this.reset()));
    this.modelBtn?.addEventListener('click', () => this.analyseModel());
    this.modelInput?.addEventListener('keydown', (e) => { if (e.key === 'Enter') this.analyseModel(); });
  }

  switchTab(tab) {
    this.mode = tab;
    this.reset();
    this.tabs.forEach(t => t.classList.toggle('skipper-ai-tab--active', t.dataset.tab === tab));
    if (tab === 'photo') {
      this.uploadZone?.classList.remove('hidden');
      this.modelPanel?.classList.add('hidden');
    } else {
      this.uploadZone?.classList.add('hidden');
      this.modelPanel?.classList.remove('hidden');
    }
  }

  handleFile(file) {
    if (!file || !file.type.startsWith('image/')) {
      this.showError('Please upload an image file (JPG, PNG, HEIC).');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      this.showError('Image must be under 20MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      this.imageBase64 = e.target.result.split(',')[1];
      this.imageMediaType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      if (this.previewImg) this.previewImg.src = e.target.result;
      this.uploadZone?.classList.add('hidden');
      this.preview?.classList.remove('hidden');
      this.errorState?.classList.add('hidden');
    };
    reader.readAsDataURL(file);
  }

  getEndpoint() {
    if (this.proxyUrl) return this.proxyUrl;
    if (this.apiKey) return 'https://api.anthropic.com/v1/messages';
    return null;
  }

  getHeaders() {
    if (this.proxyUrl) return { 'Content-Type': 'application/json' };
    return {
      'Content-Type': 'application/json',
      'x-api-key': this.apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    };
  }

  async callAPI(messages) {
    const endpoint = this.getEndpoint();
    if (!endpoint) throw new Error('Estimator not configured. Please call us for a quote.');
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ model: 'claude-sonnet-4-6', max_tokens: 600, messages }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error?.message || `API error ${res.status}`);
    }
    return res.json();
  }

  async analyseImage() {
    if (!this.imageBase64) return;
    this.showLoading();
    const prompt = `You are an expert estimator for marine and automotive sun shade screens made by Skipper Screens Australia.

Analyse this photo and estimate the total glass windscreen area that would need to be covered with sun shade screens.

Count all forward-facing and side windows visible. Be conservative — don't include hatches or portholes, only windscreen glass.

Respond ONLY with valid JSON (no markdown, no explanation):
{
  "estimated_sqm": <number, 1 decimal place>,
  "window_count": <integer>,
  "vessel_type": "<e.g. Centre Console, Cabin Cruiser, SUV, 4WD>",
  "confidence": "<low|medium|high>",
  "notes": "<one sentence: what you can see and any assumptions>"
}

Reference points: centre console windscreen ≈ 1.0–2.5 m², full cabin cruiser ≈ 3–7 m², typical SUV ≈ 0.8–1.4 m².`;

    try {
      const data = await this.callAPI([{
        role: 'user',
        content: [
          { type: 'image', source: { type: 'base64', media_type: this.imageMediaType, data: this.imageBase64 } },
          { type: 'text', text: prompt },
        ],
      }]);
      this.showResult(this.parseResponse(data));
    } catch (err) {
      this.showError(err.message || 'Something went wrong. Please try again or call us.');
    }
  }

  async analyseModel() {
    const model = this.modelInput?.value.trim();
    if (!model) {
      this.modelInput?.focus();
      return;
    }
    this.showLoading('Looking up your model...');
    const prompt = `You are an expert estimator for marine and automotive sun shade screens made by Skipper Screens Australia.

The customer has told you their vessel or vehicle: "${model.replace(/"/g, '\\"')}"

Using your knowledge of this model, estimate the total glass windscreen area that would need sun shade screens. Include the front windscreen and any side windscreen panels. Do not include hatches, portholes, or rear windows.

If you recognise the model, give a specific estimate. If you don't recognise it, give a reasonable estimate for the vessel category (e.g. centre console, cabin cruiser, SUV) and set confidence to "low".

Respond ONLY with valid JSON (no markdown, no explanation):
{
  "estimated_sqm": <number, 1 decimal place>,
  "window_count": <integer>,
  "vessel_type": "<model name or category>",
  "confidence": "<low|medium|high>",
  "notes": "<one sentence about typical dimensions and assumptions for this model>"
}`;

    try {
      const data = await this.callAPI([{ role: 'user', content: prompt }]);
      this.showResult(this.parseResponse(data));
    } catch (err) {
      this.showError(err.message || 'Something went wrong. Please try again or call us.');
    }
  }

  parseResponse(data) {
    const text = data.content?.[0]?.text || '';
    if (!text) throw new Error('No response from AI. Please try again.');
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) throw new Error('Could not read AI response. Please try again.');
    return JSON.parse(jsonMatch[0]);
  }

  showResult(data) {
    this.loadingState?.classList.add('hidden');
    const sqm = parseFloat(data.estimated_sqm) || 1;
    const rawPrice = sqm * this.pricePerSqm;
    const estimatedPrice = Math.max(rawPrice, this.minPrice);
    const lowPrice = Math.round(estimatedPrice * 0.9);
    const highPrice = Math.round(estimatedPrice * 1.15);
    const confLabel = { low: 'Rough estimate', medium: 'Good estimate', high: 'Confident estimate' }[data.confidence] || 'Estimate';
    const confClass = { low: 'confidence--low', medium: 'confidence--medium', high: 'confidence--high' }[data.confidence] || '';
    const resultEl = this.querySelector('[data-result-content]');
    if (resultEl) {
      resultEl.innerHTML = `
        <div class="skipper-ai-result__header">
          <div class="skipper-ai-result__confidence ${confClass}">${confLabel}</div>
          <p class="skipper-ai-result__vessel">${this.escHtml(data.vessel_type || 'Your vessel')}</p>
        </div>
        <div class="skipper-ai-result__stats">
          <div class="skipper-ai-result__stat">
            <span class="skipper-ai-result__stat-value">${sqm.toFixed(1)} m²</span>
            <span class="skipper-ai-result__stat-label">Estimated Glass Area</span>
          </div>
          <div class="skipper-ai-result__stat">
            <span class="skipper-ai-result__stat-value">${data.window_count || '–'}</span>
            <span class="skipper-ai-result__stat-label">Window Panels</span>
          </div>
          <div class="skipper-ai-result__stat skipper-ai-result__stat--price">
            <span class="skipper-ai-result__stat-value">$${lowPrice}–$${highPrice}</span>
            <span class="skipper-ai-result__stat-label">Estimated Price (AUD)</span>
          </div>
        </div>
        <p class="skipper-ai-result__notes">${this.escHtml(data.notes || '')}</p>
        <p class="skipper-ai-result__disclaimer">AI estimate only — final price confirmed after professional measurement.</p>
        <div class="skipper-ai-result__actions">
          <a href="${this.bookingUrl}" class="skipper-ai-result__btn skipper-ai-result__btn--primary">Book Custom Fitment</a>
          <a href="${this.shopUrl}" class="skipper-ai-result__btn skipper-ai-result__btn--secondary">Browse Stock Products</a>
        </div>`;
    }
    this.resultState?.classList.remove('hidden');
  }

  showLoading(msg) {
    const p = this.loadingState?.querySelector('p');
    if (p) p.textContent = msg || 'Reading your windscreen...';
    this.analyseBtn?.setAttribute('disabled', 'true');
    this.loadingState?.classList.remove('hidden');
    this.resultState?.classList.add('hidden');
    this.errorState?.classList.add('hidden');
  }

  showError(msg) {
    this.analyseBtn?.removeAttribute('disabled');
    this.loadingState?.classList.add('hidden');
    if (this.errorState) {
      const msgEl = this.errorState.querySelector('[data-error-msg]');
      if (msgEl) msgEl.textContent = msg;
      this.errorState.classList.remove('hidden');
    }
  }

  reset() {
    this.imageBase64 = null;
    this.imageMediaType = null;
    if (this.fileInput) this.fileInput.value = '';
    if (this.previewImg) this.previewImg.src = '';
    if (this.modelInput) this.modelInput.value = '';
    this.preview?.classList.add('hidden');
    if (this.mode === 'photo') this.uploadZone?.classList.remove('hidden');
    this.resultState?.classList.add('hidden');
    this.errorState?.classList.add('hidden');
    this.loadingState?.classList.add('hidden');
    this.analyseBtn?.removeAttribute('disabled');
  }

  escHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}

customElements.define('skipper-ai-estimator', SkipperAIEstimator);
